import React, { useEffect } from 'react';
import { Package, Shield, Users, Heart, Handshake } from 'lucide-react';
import { observeNewElements } from '../utils/scrollAnimations';

const AboutPage = () => {
  useEffect(() => {
    observeNewElements();
  }, []);

  const stats = [
    { number: '10K+', label: 'Pairs Verified', icon: Package },
    { number: '100%', label: 'Authentic', icon: Shield },
    { number: 'Bronx', label: 'Born & Built', icon: Heart },
    { number: 'Local', label: 'Pickup Available', icon: Users }
  ];

  const values = [
    {
      icon: Shield,
      title: 'Verified Always',
      description: 'Every pair legit-checked before it ships. No reps, no shortcuts, no exceptions.'
    },
    {
      icon: Handshake,
      title: 'Fair Pricing',
      description: 'Transparent markup. No hidden fees. We keep sneakers moving, not collecting dust on shelves.'
    },
    {
      icon: Users,
      title: 'Community First',
      description: 'Built by the Bronx, for sneakerheads everywhere. Tag @bxkicks_28 to get featured.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="about-page">
      {/* Hero Section */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center fade-up">
            <h1 className="text-5xl font-bold text-gray-900 mb-6" data-testid="about-title">
              About BX KICKS
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed" data-testid="about-description">
              Started with a trunk. Built on trust. Bronx-born sneaker resale by Paul Arzola—lifelong collector, verified reseller, no games.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-brand-purple text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="text-center fade-up stagger-item">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 bg-white bg-opacity-10 rounded-2xl">
                      <IconComponent className="w-8 h-8" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold mb-2" data-testid={`stat-number-${index}`}>
                    {stat.number}
                  </div>
                  <div className="text-gray-300" data-testid={`stat-label-${index}`}>
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Paul's Story */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="fade-up">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">The Story</h2>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>
                  My love for sneakers started at around 6 or 7 years old. My dad would get me sneakers on his way home from business trips; from that moment on I had a sneaker collection before I realized it.
                </p>
                <p>
                  Sneakers have become a lifestyle for me. Accessorizing my fits with a matching pair. Buying sneakers that told a story became a hobby. Snatching pairs during low points brought brightness to my days, bringing a new found love for kicks.
                </p>
                <p>
                  BX KICKS started after being laid off and having a baby to tend for. It has become a platform to connect with other sneaker lovers. I'm not here to tax people; I'm here to keep sneakers moving, make high profile releases more accessible when possible, and represent the Bronx with pride.
                </p>
                <p className="font-semibold text-gray-900">
                  — Paul Arzola, Founder
                </p>
              </div>
            </div>
            <div className="relative fade-up">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://res.cloudinary.com/dm8rx6sks/image/upload/v1762203424/sneaker-store/branding/paul-arzola-photo.jpg"
                  alt="Paul Arzola with sneaker collection"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-brand-purple text-white p-6 rounded-2xl shadow-xl">
                <div className="text-3xl font-bold">3+ Years</div>
                <div className="text-sm text-gray-200">In the Game</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Values */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-up">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How We Move</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Built on three pillars that keep the culture real.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div key={index} className="bg-white p-8 rounded-2xl shadow-sm fade-up stagger-item">
                  <div className="w-14 h-14 bg-brand-purple/10 rounded-xl flex items-center justify-center mb-6">
                    <IconComponent className="w-7 h-7 text-brand-purple" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Community Section */}
      <div className="py-20 bg-brand-purple text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Users className="w-16 h-16 mx-auto mb-6 opacity-90" />
          <h2 className="text-4xl font-bold mb-6">Built by the Community</h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Tag <a href="https://www.instagram.com/bxkicks_28/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white font-semibold">@bxkicks_28</a> with your pickup and get featured. We're stronger together.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://www.instagram.com/bxkicks_28/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white text-brand-purple px-8 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
            >
              Follow @bxkicks_28
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;